import { on, unByKey } from '@disy/lib/event-util';
import { px } from '@disy/lib/px';
import { throttle } from '@disy/lib/throttle';

import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import { applyGlobalCursor } from 'cadenza/utils/global-cursor/global-cursor';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getActiveWorksheetId } from 'cadenza/workbook/workbook-core-selectors.state';
import {
  MIN_VIEW_SIZE_IN_CELLS,
  VIEW_TYPES_WITH_SMALLER_MIN_SIZE,
} from 'cadenza/workbook/worksheet/worksheet-grid/view-resizing';
import {
  GRID_SIZE,
  dispatchLayoutChangeEvent,
  getGridArea,
  intersectsRect,
  startAdjusting,
  stopAdjusting,
  toggleViewBorder,
} from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';
import { calculateWorksheetViewBorders } from 'cadenza/workbook/worksheet/worksheet-util';

const MOVING_VIEW_CSS_CLASS = 'is-moving';
const IS_COLLIDING_WHILE_MOVING_CSS_CLASS = 'moving-collision';
const SWAP_VIEW_CSS_CLASS = 'swap-view';

export function handleViewMoving(pointerdownEvent, view, grid, viewRect, gridRect, globalCursorSourceId) {
  const { clientX: x, clientY: y } = pointerdownEvent;
  // initial position of the view before it is moved around
  const position = startAdjusting(grid, view, MOVING_VIEW_CSS_CLASS);

  const otherViews = grid.views.filter((v) => v !== view);

  // Block interaction with every view iframe for the time of the view-moving, because otherwise the
  // pointer might hover over the iframe and start interacting with it, which is really bad.
  view.content.style.pointerEvents = 'none';
  otherViews.forEach((v) => {
    if (v.content) {
      v.content.style.pointerEvents = 'none';
    }
  });

  // In case of only pointerdown - pointerup (click without pointermoving) on title moving should be stopped.
  let isClick = true;
  // If the view is currently colliding with another view (and therefore cannot be moved there).
  let isColliding = false;
  // Other view where the mouse is currently above. Current view might get swapped with this target view.
  let targetView;
  // If we can swap views (may not be allowed because target view is too small)
  let isSwapAllowed;

  const eventKeys = [
    on(
      window,
      'pointermove',
      throttle(
        (event) => {
          isClick = false;
          const { clientX: newX, clientY: newY } = event;
          // deltas between initial mouse down and current mouse position
          const dx = newX - x;
          const dy = newY - y;

          // user should be able to freely move the view, even outside the grid (to make view swapping nicer)
          view.style.top = px(position.top + dy);
          view.style.right = px(position.right - dx);
          view.style.bottom = px(position.bottom - dy);
          view.style.left = px(position.left + dx);

          // show borders around other views to show their dimensions
          otherViews.forEach((v) => toggleViewBorder(v, true));

          // The drop preview marks the position on the grid where the view will "land" if dragging
          // would stop now.
          grid.toggleDropPreviewVisibility(true);

          // targetView is the one which the user currently is dragging the moved view on
          targetView = otherViews.find((v) => isPointInsideRect({ x: newX, y: newY }, v.getBoundingClientRect()));
          isColliding = otherViews.some((v) => intersectsRect(v.getBoundingClientRect(), view.getBoundingClientRect()));

          // We have 3 cases to discern here:
          // 1. mouse pointer is over other view: swapping available
          // 2. collision of two views (while mouse pointer itself is _not_ on top of other view): show not-allowed style
          // 3. normal moving of the view around in the grid

          if (targetView) {
            // Check if both the moved view and the target view are actually suitable for swapping: only
            // if they're big enough (for example, text views might be smaller and can only be swapped
            // than with another text/image view).
            isSwapAllowed = checkIfSwapIsPossible(view, targetView);

            if (isSwapAllowed) {
              applyGlobalCursor('grabbing', globalCursorSourceId);
              view.classList.toggle(IS_COLLIDING_WHILE_MOVING_CSS_CLASS, false);
              grid.classList.toggle(SWAP_VIEW_CSS_CLASS, true);
              grid.toggleSwapPreviewVisibility(true);
              grid.setSwapPreviewLayout(view.layout);
              grid.setDropPreviewLayout(targetView.layout);
            } else {
              applyGlobalCursor('not-allowed', globalCursorSourceId);
              view.classList.toggle(IS_COLLIDING_WHILE_MOVING_CSS_CLASS, true);
              grid.classList.toggle(SWAP_VIEW_CSS_CLASS, false);
              grid.toggleSwapPreviewVisibility(false);
              grid.setDropPreviewLayout(view.layout);
            }
          } else if (isColliding) {
            applyGlobalCursor('not-allowed', globalCursorSourceId);
            view.classList.toggle(IS_COLLIDING_WHILE_MOVING_CSS_CLASS, true);
            grid.classList.toggle(SWAP_VIEW_CSS_CLASS, false);
            grid.toggleSwapPreviewVisibility(false);

            // marks the old position of the currently dragged view, because it would return there currently
            grid.setDropPreviewLayout(view.layout);
          } else {
            view.classList.toggle(IS_COLLIDING_WHILE_MOVING_CSS_CLASS, false);
            grid.classList.toggle(SWAP_VIEW_CSS_CLASS, false);
            grid.toggleSwapPreviewVisibility(false);

            // Make sure the drop preview stays inside the bounds of the grid as the view itself can now
            // be moved outside of it.
            const [rowStart, colStart, rowEnd, colEnd] = ensureViewDimensions(
              view,
              getGridArea(view, gridRect.width, gridRect.height, true),
            );

            // Make sure that there is no collision with an existing view. This can happen if the view
            // is dragged outside of the grid and the drop preview then overlaps an existing view.
            const isDropPreviewColliding = otherViews.some((v) =>
              __areViewsOverlapping__([rowStart, colStart, rowEnd, colEnd], v.layout),
            );

            if (isDropPreviewColliding) {
              view.classList.toggle(IS_COLLIDING_WHILE_MOVING_CSS_CLASS, true);
              // change position of drop preview to old view location
              grid.setDropPreviewLayout(view.layout);
              applyGlobalCursor('not-allowed', globalCursorSourceId);
              isColliding = true;
            } else {
              // If there is no collision, we can just use the calculated coordinates to position the
              // drop preview properly.
              grid.setDropPreviewLayout([rowStart, colStart, rowEnd, colEnd]);
              applyGlobalCursor('grabbing', globalCursorSourceId);
            }
          }

          // Trailing execution must be avoided, otherwise view position might already have been sent to
          // server and updated there, but view is moved one more time on the client which leads to
          // weird behavior.
        },
        30,
        { trailing: false },
      ),
    ),

    on(window, 'pointerup', () => {
      applyGlobalCursor('', globalCursorSourceId);
      unByKey(eventKeys);
      if (!isClick) {
        if (targetView && isSwapAllowed) {
          // swap views
          const viewLayout = [...view.layout];
          view.setLayoutOnClient(targetView.layout);
          targetView.setLayoutOnClient(viewLayout);
          getStore().dispatch(
            executeCommand(
              Command.COMPOSITE_COMMAND({
                commands: [
                  Command.SET_VIEW_LAYOUT({
                    viewId: view.viewId,
                    layout: view.layout,
                  }),
                  Command.SET_VIEW_LAYOUT({
                    viewId: targetView.viewId,
                    layout: targetView.layout,
                  }),
                ],
              }),
            ),
          );
          grid.classList.toggle(SWAP_VIEW_CSS_CLASS, false);
          grid.toggleSwapPreviewVisibility(false);
          dispatchLayoutChangeEvent(view);
        } else if (!isColliding) {
          // When the view is colliding, there is no change (the view just stays at its previous
          // position). We only update the view's position if there is no collision.
          view.setLayoutOnClientAndServer(grid.getDropPreviewLayout());
          dispatchLayoutChangeEvent(view);
        }
      }
      const showBorders = calculateWorksheetViewBorders(
        getActiveWorksheetId(getStore().getState()),
        getStore().getState(),
      );
      if (!showBorders) {
        otherViews.forEach((v) => toggleViewBorder(v, false));
      }
      view.classList.remove(IS_COLLIDING_WHILE_MOVING_CSS_CLASS);
      stopAdjusting(grid, view, MOVING_VIEW_CSS_CLASS);
      grid.toggleDropPreviewVisibility(false);
      // re-enable interaction with the iframes
      view.content.style.pointerEvents = 'auto';
      otherViews.forEach((v) => {
        if (v.content) {
          v.content.style.pointerEvents = 'auto';
        }
      });
    }),
  ];
}

function isPointInsideRect(point, rect) {
  return rect.left <= point.x && point.x <= rect.right && rect.top <= point.y && point.y <= rect.bottom;
}

function ensureViewDimensions(view, [rowStart, colStart, rowEnd, colEnd]) {
  const newColSpan = colEnd - colStart;
  const newRowSpan = rowEnd - rowStart;
  const currentColSpan = view.layout[3] - view.layout[1];
  const currentRowSpan = view.layout[2] - view.layout[0];

  /*
  getGridArea() calculates layout values for the view by dividing position offsets with column width
  or row height and rounds the values. Rounding works great for resizing since we have movement of
  one or max 2 edges. When moving view the whole rectangle is moved and rounding for highlighting
  causes weird behaviour in 2 cases: grid cells are very small or grid cells are very wide.
  This fixes those cases by expanding highlighted area to always be of same size as the view.
   */
  if (newColSpan < currentColSpan) {
    colEnd++;
  }
  if (newRowSpan < currentRowSpan) {
    rowEnd++;
  }
  colEnd = Math.min(colEnd, GRID_SIZE - 1); // subtract 1 because CSS size is based on 1 (layout is based on 0)
  rowEnd = Math.min(rowEnd, GRID_SIZE - 1);
  return [rowStart, colStart, rowEnd, colEnd];
}

function isLayoutBiggerThanMinimum([rowStart, colStart, rowEnd, colEnd]) {
  if (colEnd - colStart < MIN_VIEW_SIZE_IN_CELLS - 1 || rowEnd - rowStart < MIN_VIEW_SIZE_IN_CELLS - 1) {
    return false;
  }
  return true;
}

function checkIfSwapIsPossible(view1, view2) {
  const isBigView1 = isLayoutBiggerThanMinimum(view1.layout);
  const isBigView2 = isLayoutBiggerThanMinimum(view2.layout);
  // If both views are big or both are small, allow the swap.
  if ((isBigView1 && isBigView2) || (!isBigView1 && !isBigView2)) {
    return true;
  } else if (
    VIEW_TYPES_WITH_SMALLER_MIN_SIZE.includes(view1.type) &&
    VIEW_TYPES_WITH_SMALLER_MIN_SIZE.includes(view2.type)
  ) {
    // If both views are of a view type capable of small sizes, we can also swap.
    return true;
  }
  return false;
}

/**
 * Returns true if the two views are overlapping at some grid cell(s)
 *
 * @param {import('cadenza/workbook/worksheet/dto/grid-area-dto').GridAreaDto} v1
 * @param {import('cadenza/workbook/worksheet/dto/grid-area-dto').GridAreaDto} v2
 * @return whether the views overlap
 */
// Exported for testing
export function __areViewsOverlapping__(
  [v1rowStart, v1colStart, v1rowEnd, v1colEnd],
  [v2rowStart, v2colStart, v2rowEnd, v2colEnd],
) {
  return !(v1rowStart > v2rowEnd || v1rowEnd < v2rowStart || v1colStart > v2colEnd || v1colEnd < v2colStart);
}
